import {
  Directive,
  ElementRef,
  HostListener,
  Input,
  OnInit,
} from '@angular/core';
import { AbstractControl } from '@angular/forms';
import { defaults, isUndefined, trimStart } from 'lodash';

export interface IFormatAsNumberOptions {
  blurOnEnter?: boolean;
  decimalDigits?: number;
  locale?: string;
  onBlur?: Function;
  max?: number;
  min?: number;
  allowNegative?: boolean;
  decimalSeparator?: string;
}

const formatAsNumberOptionDefaults: IFormatAsNumberOptions = {
  blurOnEnter: true,
  decimalDigits: 2,
  locale: 'en-US',
  onBlur: (value) => value,
  max: Infinity,
  min: -Infinity,
  allowNegative: false,
  decimalSeparator: '.',
};

@Directive({
  selector: '[formatAsNumber]',
})
export class FormatAsNumberDirective implements OnInit {
  @Input() formatAsNumber: IFormatAsNumberOptions;
  @Input() formControl: AbstractControl;

  private _formatAsNumberOptions: IFormatAsNumberOptions;
  private _el: HTMLInputElement;
  private _prevValue: string;
  private _prevSelectionStart: number;
  private _prevSelectionEnd: number;

  constructor(private _elementRef: ElementRef) {}

  ngOnInit() {
    this._el = this._elementRef.nativeElement;
    this._prevValue = this._el.value;

    this._formatAsNumberOptions = defaults(
      {},
      this.formatAsNumber,
      formatAsNumberOptionDefaults
    );
  }

  @HostListener('focus', ['$event.target.value'])
  onFocusEvent(value: string) {
    if (this._el) {
      this._el.value = this.parseNumber(value);
    }

    setTimeout(() => {
      if (this._el) {
        this._el.select();
      }
    });
  }

  @HostListener('blur', ['$event.target.value'])
  onBlurEvent(value: string) {
    if (this._el) {
      this._el.value = this._formatAsNumberOptions.onBlur(
        this.parseNumber(value)
      );
    }
  }

  @HostListener('keydown', ['$event'])
  onKeydownEvent(evt: KeyboardEvent) {
    this.setPrevInfo();
  }

  @HostListener('paste')
  onPasteEvent() {
    this.setPrevInfo();
  }

  @HostListener('input', ['$event'])
  onInputEvent(evt: KeyboardEvent) {
    if (this._el) {
      const decimalSeparator = this._formatAsNumberOptions.decimalSeparator;
      let value = (evt.target as any).value || '0';

      // key === '.' on numpad (regional decimal)
      if (evt.which === 110 && !!decimalSeparator) {
        value = value.replace(evt.key, decimalSeparator);
      }

      const parsedValue: string = this.parseNumber(value);
      const currentSelectionStart: number = this._el.selectionStart;

      let modifiedSelectionStart;

      if (value === this._formatAsNumberOptions.decimalSeparator) {
        modifiedSelectionStart = parsedValue.length;
      } else if (parsedValue.length > this._prevValue.length) {
        modifiedSelectionStart = currentSelectionStart;
      } else if (parsedValue.length < this._prevValue.length) {
        modifiedSelectionStart =
          currentSelectionStart +
          (this._prevValue.length - parsedValue.length) -
          1;
      } else if (value.length < this._prevValue.length) {
        modifiedSelectionStart = currentSelectionStart + 1; // (this._prevValue.length - value.length);
      } else if (parsedValue.length === this._prevValue.length) {
        modifiedSelectionStart = this._prevSelectionEnd;
      } else {
        modifiedSelectionStart = currentSelectionStart;
      }

      this._el.value = parsedValue;

      this._el.selectionStart = modifiedSelectionStart;
      this._el.selectionEnd = modifiedSelectionStart;
    }
  }

  setPrevInfo() {
    if (this._el) {
      this._prevValue = this._el.value;

      this._prevSelectionStart = this._el.selectionStart;
      this._prevSelectionEnd = this._el.selectionEnd;
    }
  }

  parseNumber(value: string): string {
    const isNegative: boolean = value.charAt(0) === '-';
    const decimalSeparator: string = this._formatAsNumberOptions
      .decimalSeparator;
    let valueData: string[];

    if (decimalSeparator) {
      valueData = value
        .split(decimalSeparator)
        .map((v) => v.replace(/\D/g, ''));
    } else {
      valueData = [value.replace(/\D/g, '')];
    }

    const integerPart: string = valueData[0] || '0';
    const trimmedIntegerPart: string =
      parseInt(integerPart, 10) > 0 ? trimStart(integerPart, '0') : '0';
    const fractionPart: string[] = valueData.slice(1);

    let integer: string = isNegative
      ? '-' + trimmedIntegerPart
      : trimmedIntegerPart;
    let fraction: string;

    if (fractionPart.length) {
      if (fractionPart[0] === '' && fractionPart.length === 1) {
        fraction = '';
      } else {
        fraction = fractionPart.join('');
      }
    }

    return !isUndefined(fraction)
      ? [
          integer,
          fraction.substr(0, this._formatAsNumberOptions.decimalDigits),
        ].join(decimalSeparator)
      : integer;
  }
}
